import type { User } from '../../types';
import type { ChangeRequest, ChangeWorkGroup } from '../../types/changes';
import { isChangeManager } from './changePermissions';

export const IMPLEMENTATION_GATE_HELP =
  'Chamado externo CINFRA que trava Implementar → Revisar até concluir no Incidente. ' +
  'Abra pela aba Transporte da mudança (não no passo Confirme as subtarefas).';

export function isInfraIncidentArea(area: ChangeWorkGroup): boolean {
  const kind = String(area.incident_kind || '')
    .trim()
    .toLowerCase();
  if (kind === 'infra_nuvem' || kind === 'infra_hosting') {
    return true;
  }
  if (!area.open_in_incidents || !area.incidents_group_id) {
    return false;
  }
  const name = (area.name || '').toUpperCase();
  return name.includes('CINFRA') && !name.includes('CSI');
}

export function changeHasInfraImplementationGate(change: ChangeRequest): boolean {
  return (change.subtasks || []).some((item) => Boolean(item.gates_implementar));
}

export function infraImplementationGateSubtasks(change: ChangeRequest) {
  return (change.subtasks || []).filter((item) => Boolean(item.gates_implementar));
}

/** Áreas Infra ainda sem chamado de gate nesta mudança (toggle na criação). */
export function infraGateEligibleAreas(
  areas: ChangeWorkGroup[],
  change: ChangeRequest,
): ChangeWorkGroup[] {
  const sent = gateGroupIds(change);
  return areas.filter(
    (area) =>
      area.open_in_incidents &&
      area.incidents_group_id &&
      isInfraIncidentArea(area) &&
      !sent.has(area.group_id),
  );
}

function gateGroupIds(change: ChangeRequest): Set<number> {
  return new Set(
    (change.subtasks || [])
      .filter(
        (item) =>
          item.gates_implementar &&
          item.group_id != null &&
          !item.invalidated_at,
      )
      .map((item) => Number(item.group_id)),
  );
}

export function partitionInfraGateSubtasks(change: ChangeRequest) {
  const all = infraImplementationGateSubtasks(change);
  const active = all.filter((item) => !item.invalidated_at);
  const history = all.filter((item) => Boolean(item.invalidated_at));
  return { active, history };
}

/** Todos os grupos ativos da mudança que abrem chamado no Incidente (cadastro Externa). */
export function externalIncidentWorkGroups(areas: ChangeWorkGroup[]): ChangeWorkGroup[] {
  return areas
    .filter(
      (area) =>
        area.is_active !== false && area.open_in_incidents && area.incidents_group_id,
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
}

export function areaHasActiveImplementationGate(
  change: ChangeRequest,
  groupId: number,
): boolean {
  return gateGroupIds(change).has(groupId);
}

export type GateIncidentDraftHint = {
  workflow_id?: number | '';
  incidents_group_id?: number;
};

/**
 * Vincula o rascunho do Incidente (fila + fluxo) ao grupo da mudança (cadastro externo).
 * Uma área elegível: sempre ela. Várias: fila padrão do cadastro; se ambíguo, desempata pelo fluxo.
 */
export function inferChangeWorkGroupForGateDispatch(
  eligible: ChangeWorkGroup[],
  draft: GateIncidentDraftHint,
): ChangeWorkGroup | null {
  if (!eligible.length) return null;
  if (eligible.length === 1) return eligible[0];

  const queueId = draft.incidents_group_id;
  if (!queueId) return null;

  const byQueue = eligible.filter((area) => Number(area.incidents_group_id) === Number(queueId));
  if (byQueue.length === 1) return byQueue[0];
  if (!byQueue.length) return null;

  const workflowId = draft.workflow_id ? Number(draft.workflow_id) : null;
  if (workflowId) {
    const byWorkflow = byQueue.filter((area) => {
      const configured = area.incidents_workflow_id;
      if (!configured) return true;
      return Number(configured) === workflowId;
    });
    if (byWorkflow.length === 1) return byWorkflow[0];
  }

  const withoutWorkflowPin = byQueue.filter((area) => !area.incidents_workflow_id);
  if (withoutWorkflowPin.length === 1) return withoutWorkflowPin[0];

  return null;
}

export function transportImplementGateEligibleAreas(
  areas: ChangeWorkGroup[],
  change: ChangeRequest,
): ChangeWorkGroup[] {
  const sent = gateGroupIds(change);
  return areas
    .filter(
      (area) =>
        area.is_active !== false &&
        area.open_in_incidents &&
        area.incidents_group_id &&
        !sent.has(area.group_id),
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
}

export function canOfferInfraGateDispatch(change: ChangeRequest): boolean {
  if (change.can_create_subtask === false) return false;
  if (change.status !== 'transporte' && change.status !== 'implementar') return false;
  return true;
}

export type TransportInfraGateUser = {
  id?: number;
  role?: string;
  can_manage_changes?: boolean;
};

/** Mesma regra do backend may_open_implementation_gate (Transporte / Implementar). */
export function canOpenTransportInfraGate(
  change: ChangeRequest,
  user: TransportInfraGateUser | null | undefined,
): boolean {
  if (!canOfferInfraGateDispatch(change) || !user?.id) return false;
  if (isChangeManager(user as User)) return true;
  if (typeof change.can_open_implementation_gate === 'boolean') {
    return change.can_open_implementation_gate;
  }
  if (change.change_owner_id === user.id) return true;
  if (change.created_by === user.id) return true;
  return false;
}
